import React, { useEffect, useMemo, useState } from 'react';
import ReferenceImagesModal from './ReferenceImagesModal';
import { useReadOnly } from '../contexts/ReadOnlyContext';
import { getAppendixImages } from '../utils/storage';
import './SubcardEditModal.css';

// Per-table edit modal: rename heading, revert, pick examined-by + manual/drone,
// attach reference images, set scanline/position counts, edit notes, or delete.
// variant="analysis" is the deformation "Edit Analysis": fixed heading (no
// revert), appendix-only reference images, no counters, OOV auto-note hint.
// Props:
//   table          : the UT table object
//   inspection     : current inspection (for ndtOperators + manualOrDrone)
//   inspectionId   : for the reference images modal
//   onCancel       : () => void
//   onSave         : async (patch) => void
//   onDelete       : () => void

// ── Component-Measurement → counter config lookup ───────────────────
// Each component+measurement combo either uses scanlines (column-style
// layout) or positions (point-style layout), or neither. The `default`
// is the seed count used when the sub-card is first created — the user
// can change it via this modal afterwards. Keys are lowercased
// "component||measurement".
export const SCANLINE_OPTIONS = [4, 8, 10, 12, 14, 16, 18, 20, 22, 24];
export const POSITION_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

const COUNTER_MAP = {
  'floor (external)||outside edge of bottom': { mode: 'scanlines', default: 8 },
  'floor (internal)||annular plates':         { mode: 'positions', default: 5 },
  'floor (internal)||flat bottom':            { mode: 'scanlines', default: 8 },
  'floor (internal)||floor sump':             { mode: 'positions', default: 5 },
  'floor (internal)||membrane plates':        { mode: 'positions', default: 5 },
  'heads (external)||front head':             { mode: 'scanlines', default: 8 },
  'heads (external)||rear head':              { mode: 'scanlines', default: 8 },
  'heads (internal)||front head':             { mode: 'scanlines', default: 8 },
  'heads (internal)||rear head':              { mode: 'scanlines', default: 8 },
  'nozzles (external)||roof nozzles':         { mode: 'positions', default: 4 },
  'nozzles (external)||shell nozzles':        { mode: 'positions', default: 4 },
  'nozzles (internal)||roof nozzles':         { mode: 'positions', default: 4 },
  'nozzles (internal)||shell nozzles':        { mode: 'positions', default: 4 },
  'pipes (external)||piping systems':         { mode: 'positions', default: 4 },
  'pipes (internal)||bubbling pipes':         { mode: 'positions', default: 4 },
  'pipes (internal)||heating pipes':          { mode: 'positions', default: 4 },
  'pipes (internal)||piping systems':         { mode: 'positions', default: 4 },
  'rafters||l rafters (double)':                 { mode: 'scanlines', default: 8 },
  'rafters||ipe rafters':                      { mode: 'scanlines', default: 8 },
  'rafters||t rafters':                        { mode: 'scanlines', default: 8 },
  'rafters||upn rafters':                      { mode: 'scanlines', default: 8 },
  'roof (external)||fixed roof':              { mode: 'scanlines', default: 8 },
  'roof (internal)||fixed roof':              { mode: 'scanlines', default: 8 },
  'roof (internal)||supporting columns':      { mode: 'positions', default: 4 },
  'shell (external)||shell':                  { mode: 'scanlines', default: 8 },
  'shell (external)||shell courses':          { mode: 'scanlines', default: 8 },
  'shell (internal)||shell':                  { mode: 'scanlines', default: 8 },
  'shell (internal)||shell courses':          { mode: 'scanlines', default: 8 },
  'rafters||l rafters':                        { mode: 'scanlines', default: 8 },
  'rafters||upn rafters (double)':               { mode: 'scanlines', default: 8 },
  'custom||custom':                           { mode: 'scanlines', default: 8 },
  // CW-OSI: single fixed sub-card, scanline counter (4–24).
  'floor (internal)||bottom corner weld':     { mode: 'scanlines', default: 8 },
};

function counterKey(component, measurement) {
  return `${(component || '').trim().toLowerCase()}||${(measurement || '').trim().toLowerCase()}`;
}

// Returns { mode, default } | null. Exported so InspectionDetail can seed
// the count when a sub-card is first created.
export function getCounterConfig(component, measurement) {
  return COUNTER_MAP[counterKey(component, measurement)] || null;
}

function getCounterMode(component, measurement) {
  return getCounterConfig(component, measurement)?.mode || null;
}

export default function SubcardEditModal({
  table, inspection, inspectionId, onCancel, onSave, onDelete, variant = 'table',
}) {
  const readOnly = useReadOnly();   // archived → "Show more": fields disabled, no save/delete
  const isAnalysis = variant === 'analysis';
  const [heading, setHeading]                       = useState(table.heading || '');
  const [examinedBy, setExaminedBy]                 = useState(table.examinedBy || '');
  const [manualOrDrone, setManualOrDrone]           = useState(table.manualOrDrone || '');
  const [referenceImageIds, setReferenceImageIds]   = useState(table.referenceImageIds || []);
  const [subcardImageIds, setSubcardImageIds]       = useState(table.subcardImageIds || []);
  const [notes, setNotes]                           = useState(table.notes || '');
  // Fall back to the layout default when the table doesn't yet have a
  // count saved (legacy sub-cards from before this field existed).
  const _cfg = getCounterConfig(table.component, table.measurement);
  const [scanlineCount, setScanlineCount]           = useState(
    table.scanlineCount ?? (_cfg?.mode === 'scanlines' ? _cfg.default : ''),
  );
  const [positionCount, setPositionCount]           = useState(
    table.positionCount ?? (_cfg?.mode === 'positions' ? _cfg.default : ''),
  );
  const [showRefImagesModal, setShowRefImagesModal] = useState(false);

  // Drop reference ids whose appendix image no longer exists (deleted since
  // they were saved) so "Reference Images (N)" counts only real images.
  useEffect(() => {
    let cancelled = false;
    getAppendixImages(inspectionId)
      .then((list) => {
        if (cancelled || !Array.isArray(list)) return;
        const live = new Set(list.map((img) => String(img.id)));
        setReferenceImageIds((prev) => {
          const next = prev.filter((id) => live.has(String(id)));
          return next.length === prev.length ? prev : next;
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [inspectionId]);
  const [saving, setSaving]                         = useState(false);
  const [error, setError]                           = useState('');

  // Examined-by options: NDT operators from the inspection (no DDL).
  const examinedByOptions = useMemo(() => {
    const ops = inspection?.formData?.ndtOperators;
    if (!Array.isArray(ops)) return [];
    return ops.map((op) => (typeof op === 'string' ? op : (op.name || ''))).filter(Boolean);
  }, [inspection]);

  // Manual/Drone options: derived from the inspection-level value.
  // 'manual' → ['manual']; 'drone' → ['drone']; 'both'/anything else → both choices.
  const manualOrDroneOptions = useMemo(() => {
    const v = (inspection?.formData?.manualOrDrone || '').trim().toLowerCase();
    if (v === 'manual') return ['Manual'];
    if (v === 'drone')  return ['Drone'];
    if (v === 'both' || v === 'manual & drone' || v === 'manual and drone') {
      return ['Manual', 'Drone'];
    }
    // Unknown / empty: still allow either so the user isn't blocked
    return ['Manual', 'Drone'];
  }, [inspection]);

  const counterMode = getCounterMode(table.component, table.measurement);

  const trimmedHeading = heading.trim();
  const canRevert = !!table.originalHeading && trimmedHeading !== table.originalHeading;

  function handleRevert() {
    if (!table.originalHeading) return;
    setHeading(table.originalHeading);
  }

  async function handleSave() {
    if (!trimmedHeading) {
      setError('Heading cannot be empty.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Persist whichever counter is relevant; null out the other so
      // stale values don't survive a layout change.
      const patch = {
        heading: trimmedHeading,
        examinedBy,
        manualOrDrone,
        referenceImageIds,
        subcardImageIds,
        notes: notes.trimEnd(),
        scanlineCount: counterMode === 'scanlines' && scanlineCount !== ''
          ? Number(scanlineCount) : null,
        positionCount: counterMode === 'positions' && positionCount !== ''
          ? Number(positionCount) : null,
      };
      await onSave(patch);
    } catch (err) {
      setError(err?.message || 'Failed to save.');
      setSaving(false);
    }
  }

  // Build the Component – Measurement subtitle. Falls back gracefully if
  // either piece is missing (older tables predating this metadata).
  const subtitle = [table.component, table.measurement].filter(Boolean).join(' – ');

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal subcard-edit-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">{isAnalysis ? 'Edit Analysis' : 'Edit Measurement Table'}</h2>
        {subtitle && (
          <div className="subcard-edit-subtitle">{subtitle}</div>
        )}

        {/* Heading */}
        <div className="subcard-edit-field">
          <label className="subcard-edit-label">Heading</label>
          <div className="subcard-edit-row">
            <input
              type="text"
              className="subcard-edit-input"
              value={heading}
              onChange={(e) => setHeading(e.target.value)}
              disabled={readOnly || isAnalysis}
            />
            {!isAnalysis && (
              <button
                type="button"
                className="subcard-edit-revert-btn"
                onClick={handleRevert}
                disabled={!canRevert || readOnly}
                title="Revert to original heading"
                aria-label="Revert heading"
              >
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10" />
                  <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                </svg>
              </button>
            )}
          </div>
          {!isAnalysis && table.originalHeading && (
            <div className="subcard-edit-hint">
              Default: {table.originalHeading}
            </div>
          )}
        </div>

        {/* Examined By + Manual/Drone (side-by-side) */}
        <div className="subcard-edit-grid">
          <div className="subcard-edit-field">
            <label className="subcard-edit-label">Examined By</label>
            <select
              className="subcard-edit-select"
              value={examinedBy}
              onChange={(e) => setExaminedBy(e.target.value)}
              disabled={examinedByOptions.length === 0 || readOnly}
            >
              <option value="">
                {examinedByOptions.length === 0
                  ? '— No NDT operators on inspection —'
                  : '— Select —'}
              </option>
              {examinedByOptions.map((op) => (
                <option key={op} value={op}>{op}</option>
              ))}
            </select>
          </div>

          <div className="subcard-edit-field">
            <label className="subcard-edit-label">Manual / Drone</label>
            <select
              className="subcard-edit-select"
              value={manualOrDrone}
              onChange={(e) => setManualOrDrone(e.target.value)}
              disabled={readOnly}
            >
              <option value="">— Select —</option>
              {manualOrDroneOptions.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Reference Images + (Scanlines | Positions | nothing) on one row.
            Whichever counter applies is decided by the component/measurement
            of this table. If neither applies, the right column collapses
            and the button takes the full row. */}
        <div className="subcard-edit-grid">
          <div className="subcard-edit-field">
            <label className="subcard-edit-label">Reference Images</label>
            <button
              type="button"
              className="subcard-edit-ref-btn"
              onClick={() => setShowRefImagesModal(true)}
            >
              Reference Images ({referenceImageIds.length + (isAnalysis ? 0 : subcardImageIds.length)})
            </button>
          </div>

          {counterMode === 'scanlines' && (
            <div className="subcard-edit-field">
              <label className="subcard-edit-label">Number of Scanlines</label>
              <select
                className="subcard-edit-select"
                value={scanlineCount}
                onChange={(e) => setScanlineCount(e.target.value)}
                autoFocus
                disabled={readOnly}
              >
                <option value="">— Select —</option>
                {SCANLINE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}

          {counterMode === 'positions' && (
            <div className="subcard-edit-field">
              <label className="subcard-edit-label">Number of Positions</label>
              <select
                className="subcard-edit-select"
                value={positionCount}
                onChange={(e) => setPositionCount(e.target.value)}
                autoFocus
                disabled={readOnly}
              >
                <option value="">— Select —</option>
                {POSITION_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Notes — free-text, one note per line. Auto-notes (mm units,
            negative degradation) are added on top of these by the sub-card
            preview based on the table contents. */}
        <div className="subcard-edit-field">
          <label className="subcard-edit-label">Notes</label>
          <textarea
            className="subcard-edit-input subcard-edit-textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={4}
            placeholder="One line per note"
            disabled={readOnly}
          />
          <div className="subcard-edit-hint">
            {isAnalysis
              ? 'The negative out-of-verticality auto-note is always included.'
              : 'Auto-notes (mm units, negative degradation) are added automatically when applicable.'}
          </div>
        </div>

        {error && <div className="subcard-edit-error">{error}</div>}

        <div className="modal-actions subcard-edit-actions">
          {readOnly ? (
            <div className="subcard-edit-actions-right">
              <button className="btn btn-secondary" onClick={onCancel}>Close</button>
            </div>
          ) : (
            <>
              {/* Delete is suppressed for inspection types with a fixed
                  single sub-card (CW-OSI) — the parent simply omits the
                  onDelete prop. */}
              {typeof onDelete === 'function' && (
                <button
                  className="btn btn-danger"
                  onClick={onDelete}
                  disabled={saving}
                >
                  Delete
                </button>
              )}
              <div className="subcard-edit-actions-right">
                <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !trimmedHeading}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* Reference Images picker — sub-card scoped so the generated images
            pool is visible alongside the inspection's appendix uploads. */}
        {showRefImagesModal && (
          <ReferenceImagesModal
            inspectionId={inspectionId}
            subcardId={isAnalysis ? undefined : table.id}   // analysis → appendix images only
            initialSelected={referenceImageIds}
            initialSelectedSubcardImages={subcardImageIds}
            onCancel={() => setShowRefImagesModal(false)}
            onSave={(payload) => {
              if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                setReferenceImageIds(payload.referenceImageIds || []);
                setSubcardImageIds(payload.subcardImageIds || []);
              } else {
                setReferenceImageIds(payload || []);
              }
              setShowRefImagesModal(false);
            }}
          />
        )}
      </div>
    </div>
  );
}
